export const useSeoRelatedCategories = async ({ relatedQuery, isBrandPage }: any) => {
  const { $api } = useNuxtApp();
  const route = useRoute();
  const mainStore = useMainStore();

  // Get related links of categories
  let relatedLinks = null;
  const splitFullPath = route.fullPath?.split('/');

  if (splitFullPath && splitFullPath[2] && relatedQuery) {
    const requestingLevel1Url = !isBrandPage ? `/c/${splitFullPath[2]}/` : null;

    try {
      const { data, error } = await useAsyncData('seo-related-categories', async () => {
        const response = await ($api as Record<string, any>).hybris.seocontent.getRelatedLinks(
          requestingLevel1Url,
          relatedQuery,
          mainStore.language,
        );
        return response.data;
      });

      if (data.value && !error.value) {
        relatedLinks = data.value;
      }
    } catch (e) {
      relatedLinks = null;
    }
  }

  const relatedCategories: Array<any> = [];
  if (relatedLinks?.length > 1) {
    relatedLinks.forEach((category: any) => {
      if (route.fullPath !== category.fullUrl && category.headlineOverride) {
        relatedCategories.push(category);
      }
    });
  }

  return relatedCategories;
};

export const useSeoPageContent = async ({ data }: any) => {
  const nuxtApp = useNuxtApp();
  const { $api, $i18n, $router } = nuxtApp;
  const route = unref($router.currentRoute);
  const mainStore = useMainStore(nuxtApp.$pinia);

  let configFilters = data.page.content?.config?.filters || [];
  configFilters = configFilters.map((item: { key: string }) => item.key);

  //  Check if page is brand page
  data.page.isBrandPage = configFilters.length === 1 && configFilters.includes('brand');

  //  Set default description
  if (!data.page?.content?.meta?.description) {
    data.page.content.meta.description = `${$i18n.t('productListing.defaultDescription', {
      name: data.page?.content?.additional_title ? data.page?.content?.additional_title : data.page?.content?.title,
    })} ${$i18n.t('pages.usp', {
      freeShippingThreshold: mainStore.getFreeShippingThreshold?.value,
    })}`;
  }

  // get seo content from hybris for defined urls with params
  const routeQuery = new URLSearchParams(<Record<string, string>>route.query);

  // Only include param `q` for the SeoContent call to Hybris
  const filteredRouteQuery = new URLSearchParams();
  if (routeQuery.has('q')) {
    filteredRouteQuery.append('q', routeQuery.get('q') as string);
  }

  // Hybris expects spaces encoded as '%20' instead of '+'
  const formattedFilteredRouteQuery = filteredRouteQuery.toString().replaceAll('+', '%20');

  const pageUrl = removeLanguagePrefix(route.path, nuxtApp);
  const filteredFullPath = `${pageUrl}${formattedFilteredRouteQuery ? `?${formattedFilteredRouteQuery}` : ''}`;

  const { data: response, error } = await useAsyncData(async () => {
    const { data } = await ($api as Record<string, any>).hybris.seocontent.find(filteredFullPath, mainStore.language);
    return data;
  });

  const seoContent = unref(error) ? null : unref(response);

  // If the hrefLangData's are set in Filter page's SEO contents (NEW), use those for alternate-hreflang
  if (seoContent?.hrefLangData) {
    data.page.hrefLangDataNew = seoContent.hrefLangData;
  }

  // If PLP page is activated in BO
  // receive a self-referencing canonical, display seo content from BO & adjust meta tags
  if (seoContent) {
    // currentPage param is always needed for canonical_url
    if (routeQuery.has('currentPage')) {
      filteredRouteQuery.append('currentPage', routeQuery.get('currentPage') as string);
    }

    const formattedRouteQuery = filteredRouteQuery.toString().replaceAll('+', '%20');
    data.page.content.meta.canonical_url = `${route.path}${formattedRouteQuery ? `?${formattedRouteQuery}` : ''}`;

    //  replace title, description, thumbnail image and robots
    data.page.content.meta.title = seoContent.pageTitle;
    data.page.content.meta.description = seoContent.metaDescription;
    data.page.content.thumbnail = seoContent.htmlImage?.url;
    data.page.content.headlineOverride = seoContent.headlineOverride;
    data.page.content.meta.robots = ['index', 'follow'];

    // If activated filter page has a custom htmlContent defined in the BO
    // override content_slots.a.sections of page obj with data provided in the BO.
    // Otherwise content_slots.a.sections wont be changed
    if (seoContent.htmlContent) {
      if (!data.page.content.content_slots.a) {
        data.page.content.content_slots.a = {};
      }

      data.page.content.content_slots.a.sections = [
        {
          constrained: 'l',
          padding_top: 's',
          padding_bottom: 's',
          grid: {
            items: [
              {
                components: [
                  {
                    type: 'text',
                    data: {
                      text: seoContent.htmlContent,
                    },
                  },
                ],
              },
            ],
          },
        },
      ];
    }

    data.filterPageActivated = true;
  } else {
    // If no seo content is available, remove q parameter from next/prev urls
    if (data.productList?.pagination?.prevLink) {
      data.productList.pagination.prevLink = removeParamsFromUrl(data.productList.pagination.prevLink, ['q', 'sort']);
    }

    if (data.productList?.pagination?.nextLink) {
      data.productList.pagination.nextLink = removeParamsFromUrl(data.productList.pagination.nextLink, ['q', 'sort']);
    }
  }

  if (data.productList?.facets?.length && !data.page.isBrandPage) {
    //  Filter for selected facets
    const selectedFacets = data.productList.facets.filter(
      (facet: { code: string; values: Array<{ selected: boolean }> }) =>
        facet.code !== 'category' &&
        facet.values.filter((value) => value.selected === true).length > 0 &&
        !configFilters.includes(facet.code),
    );

    if (selectedFacets.length > 0) {
      if (!seoContent) {
        //  Canonical
        data.page.content.meta.canonical_url = route.path;

        //  Robots
        data.page.content.meta.robots = ['index', 'follow'];
      }
    }
  }

  let relatedQuery = null;
  if (data.productList?.breadcrumbs) {
    const lastBreadCrumb = data.productList.breadcrumbs[data.productList.breadcrumbs.length - 1] || null;

    if (lastBreadCrumb) {
      relatedQuery = `${lastBreadCrumb.facetCode}:${lastBreadCrumb.facetValueCode}`;
    }
  }

  data.page.content.related_links = await useSeoRelatedCategories({ relatedQuery, isBrandPage: data.page.isBrandPage });

  //  Check for multicategory && category set in query
  if (route.query?.q?.includes(':multiCategory:') && route.query?.q?.includes(':category:')) {
    //  Robots
    data.page.content.meta.robots = ['noindex', 'follow'];
  }

  // If page is paginated and does not have seoContent adjust canonical_url
  if (!seoContent && route.query?.currentPage && parseInt(route.query?.currentPage?.toString()) >= 2) {
    // Canonical
    data.page.content.meta.canonical_url = `${route.path}?currentPage=${route.query.currentPage}`;
  }

  // If category/filter page has no products, don't set canonical_url and set Robots to NOINDEX
  if (!data.productList?.products?.length) {
    // Robots
    data.page.content.meta.robots = ['noindex', 'follow'];
  }

  return data;
};
